import { Link } from "react-router-dom";
import { ShieldCheck, Truck, Star, FileCheck, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { FadeUp, MaskLine } from "@/components/motion";
import { CONTACT, IMAGES } from "@/data/content";

const STATS = [
  { n: "5★", l: "Every recent Google review" },
  { n: "8", l: "Capitals served daily" },
  { n: "7 days", l: "6am–9pm support line" },
  { n: "$200", l: "Standard booking deposit" },
];

const CREDS = [
  { icon: ShieldCheck, t: "Insured operators on every job", d: "Operators carry public liability and goods-in-transit insurance for the moves they perform. Certificates available on request." },
  { icon: FileCheck, t: "10+ years of experience", d: "Every operator brings more than a decade of hands-on moving experience, with professional wrapping, handling and packing gear on every truck." },
  { icon: Truck, t: "One accountable contact", d: "We coordinate approved, insured operators Australia-wide and stay your single point of contact from quote to final delivery." },
  { icon: Star, t: "Written itemised quotes", d: "Stairs, access and heavy items spelled out before move day, in writing, every time." },
];

export default function About() {
  return (
    <main className="pt-20">
      <SEO
        title="Why Choose NextMove"
        description="Fully insured Australia-wide removalists with AFRA-standard crews, our own GPS-tracked fleet, ABN-verified business and written fixed quotes."
        path="/about"
      />
      <section className="bg-navy grain py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-amber-500">About NextMove</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.08] text-white">
            <MaskLine delay={0.1}>The calmest part</MaskLine>
            <MaskLine delay={0.22}>of <span className="text-amber-400">your move.</span></MaskLine>
          </h1>
          <FadeUp delay={0.4}>
            <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300">
              NextMove started with one truck and a simple rule: treat every load like it's our own.
              Today we run daily linehaul between every Australian capital, and the rule hasn't changed.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 py-16 sm:py-24 grid gap-12 lg:grid-cols-2 lg:items-center">
        <FadeUp>
          <div className="overflow-hidden rounded-3xl">
            <img src={IMAGES.team} alt="The NextMove Removalists team in front of branded trucks" className="h-80 sm:h-[420px] w-full object-cover" />
          </div>
        </FadeUp>
        <div>
          <FadeUp>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-navy">
              A moving company that answers the phone.
            </h2>
          </FadeUp>
          <FadeUp delay={0.1} className="mt-6 space-y-4">
            <p className="text-base leading-relaxed text-slate-600">
              We're a registered Australian service-area business (ABN {CONTACT.abn}) operating nationwide , 
              coordinating vetted, insured removal operators in every capital and major regional hub. When you
              call {CONTACT.phonePrimary}, you speak to the team that schedules the jobs, not a call centre.
            </p>
            <p className="text-base leading-relaxed text-slate-600">
              Every operator in our network is vetted, insured and brings 10+ years of hands-on moving
              experience. Every job is coordinated by us from first call to final box, and every quote
              is written and itemised before move day. That's the whole business model.
            </p>
          </FadeUp>
          <div className="mt-10 grid grid-cols-2 gap-6">
            {STATS.map((s, i) => (
              <FadeUp key={s.l} delay={i * 0.06}>
                <p className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-navy">{s.n}</p>
                <p className="mt-1 text-xs font-mono uppercase tracking-widest text-slate-500">{s.l}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-100 py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <FadeUp>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-navy">Credentials, not promises</h2>
          </FadeUp>
          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {CREDS.map((c, i) => (
              <FadeUp key={c.t} delay={i * 0.07} className="rounded-2xl border border-slate-200 bg-white p-8 sm:p-10">
                <c.icon className="h-8 w-8 text-amber-500" />
                <h3 className="mt-5 font-display text-lg font-bold text-navy">{c.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.d}</p>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2} className="mt-12 text-center">
            <Link to="/quote" data-testid="about-quote-btn"
              className="cta-gleam inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-bold text-amber-400">
              Put us to the test, get a quote <ArrowUpRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
